import { useEffect, useState } from 'react';

export interface ChartTheme {
  primary: string;
  tertiary: string;
  grid: string;
  axis: string;
  panel: string;
  text: string;
}

/**
 * Chart colours as literal values.
 *
 * Recharts renders into SVG and cannot read Tailwind classes, so the palette
 * has to be handed over concretely. Reading the live computed custom properties
 * rather than hardcoding hexes keeps the charts on the same tokens as the rest
 * of the console -- change a token and the charts follow.
 *
 * The `theme` argument is load-bearing, and so is WHEN the read happens. The
 * `.dark` class is applied by AppStyled in a useEffect, and parent effects run
 * AFTER child effects -- so on the flip render, a page-level read (render-time
 * memo or even this page's own effect) still sees the OUTGOING palette. The
 * requestAnimationFrame defers the sample until after every effect has run and
 * styles have recomputed, at the cost of one extra render per theme change.
 */
const readTokens = (): ChartTheme => {
  const read = (token: string, fallback: string) => {
    if (typeof window === 'undefined') return fallback;
    const value = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
    return value || fallback;
  };
  return {
    primary: read('--color-primary', '#0b7a55'),
    tertiary: read('--color-tertiary', '#2c6fa8'),
    grid: read('--color-soft', 'rgba(18,24,33,0.10)'),
    axis: read('--color-fg-3', '#74818d'),
    panel: read('--color-panel', '#ffffff'),
    text: read('--color-fg-1', '#121821'),
  };
};

export const useChartTheme = (theme?: string): ChartTheme => {
  // The initial synchronous read is correct: at mount, the class reflects the
  // previous commit. Only theme CHANGES need the deferred re-sample.
  const [tokens, setTokens] = useState<ChartTheme>(readTokens);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setTokens(readTokens()));
    return () => cancelAnimationFrame(frame);
  }, [theme]);
  return tokens;
};
